import {
  PlacesAutocomplete,
  SelectInput,
  TextInput,
  type PlacesAutocompleteResult,
  type SelectInputOption,
} from '@my/ui';
import { useTranslation } from 'react-i18next';
import type { AddressForm } from './lib/formMappers';

export interface AddNewAddressPanelProps {
  value: AddressForm;
  onChange: (next: AddressForm) => void;
  /** Country options driving the dropdown — passed in to avoid re-deriving here. */
  countryOptions: SelectInputOption[];
  /**
   * `true` after the user has tried to submit at least once — gates the
   * red-line decoration so a pristine form (untouched, before submit) doesn't
   * scream at the user before they've had a chance to fill it in.
   */
  showErrors: boolean;
  missingFullAddress: boolean;
  missingPostcode: boolean;
  missingCountry: boolean;
}

/**
 * Always-expanded bordered panel shown when the organisation has no
 * `mainOfficeAddressId`. Mirrors the AddressInlineEditor's expanded layout
 * (same field grid, same country select) but without the summary line — the
 * absence of an address is the cue itself, and there's nothing to summarise
 * until the user fills the form. On submit the page POSTs `/addresses` and
 * chains a PUT `/organisations` to link the new id.
 *
 * Validation: any field with content marks the panel as "intent to add". At
 * that point Address Line 1 + Country are required; missing required fields
 * red-line on submit and the page-level handler bails with a toast. The
 * `missing*` flags are computed by the parent so the same booleans drive the
 * submit-gate and the field decoration in lock-step.
 */
export function AddNewAddressPanel({
  value,
  onChange,
  countryOptions,
  showErrors,
  missingFullAddress,
  missingPostcode,
  missingCountry,
}: AddNewAddressPanelProps) {
  const { t } = useTranslation('organisation');
  const setField = <K extends keyof AddressForm>(key: K, next: AddressForm[K]) =>
    onChange({ ...value, [key]: next });

  // Picking a place repopulates every address field including lat/lng — both
  // verifies the location and saves the user typing each part by hand.
  const onPlacePicked = (place: PlacesAutocompleteResult) =>
    onChange({
      ...value,
      fullAddress: place.formattedAddress,
      city: place.city,
      postcode: place.postcode,
      countryIsoCode: place.countryIsoCode || value.countryIsoCode,
      latitude: place.latitude,
      longitude: place.longitude,
      placeId: place.placeId,
    });

  const isVerified = value.latitude != null && value.longitude != null;

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <p className="text-xs font-medium text-slate-700">{t('newAddressPanel.title')}</p>
      <div className="space-y-1">
        {/*
          Intentionally unrestricted by country — see note in
          `AddressInlineEditor` for the rationale.
        */}
        <PlacesAutocomplete
          onPlaceSelected={onPlacePicked}
          placeholder={t('newAddressPanel.placesPlaceholder')}
        />
        <p
          className={
            isVerified
              ? 'text-[11px] font-medium text-emerald-700'
              : 'text-[11px] font-medium text-amber-700'
          }
          aria-live="polite"
        >
          {isVerified
            ? t('newAddressPanel.locationVerified', {
                lat: value.latitude!.toFixed(5),
                lng: value.longitude!.toFixed(5),
              })
            : t('newAddressPanel.locationNotVerified')}
        </p>
      </div>
      <TextInput
        label={t('newAddressPanel.fullAddress')}
        name="new-fullAddress"
        autoComplete="off"
        value={value.fullAddress}
        onChange={(e) => setField('fullAddress', e.target.value)}
        error={showErrors && missingFullAddress}
        errorMessage={t('newAddressPanel.fullAddressRequired')}
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextInput
          label={t('newAddressPanel.city')}
          name="new-city"
          autoComplete="off"
          value={value.city}
          onChange={(e) => setField('city', e.target.value)}
        />
        <TextInput
          label={t('newAddressPanel.postcode')}
          name="new-postcode"
          autoComplete="off"
          value={value.postcode}
          onChange={(e) => setField('postcode', e.target.value)}
          error={showErrors && missingPostcode}
          errorMessage={t('newAddressPanel.postcodeRequired')}
        />
        <SelectInput
          label={t('newAddressPanel.country')}
          name="new-countryIsoCode"
          value={value.countryIsoCode}
          onChange={(e) => setField('countryIsoCode', e.target.value)}
          options={countryOptions}
          error={showErrors && missingCountry}
          errorMessage={t('newAddressPanel.countryRequired')}
        />
      </div>
      <p className="text-[11px] text-slate-500">{t('newAddressPanel.footer')}</p>
    </div>
  );
}
